import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Resolves a field of a competence entry (e.g. desc, title).
 *
 * Prefers the value stored inside the spider_web entry (e.g. desc_nl / desc_en)
 * so profile pages render from the questionnaire snapshot at answer time.
 * Falls back to legacy i18n keys so profiles created before the ABM change
 * keep rendering.
 *
 * Usage:
 *   {{ entry | competenceField:'desc' }}          -> desc_nl / desc_en, falls back to
 *                                                    'dh_profile:competences:description:<id>'
 *   {{ entry | competenceField:'desc':'multi' }}  -> falls back to
 *                                                    'dh_profile:competences:description:multi:<id>'
 *
 * Supported fields (with their fallback key suffix):
 *   title       -> 'dh_profile:competences:<id>' (or ':multi:' variant)
 *   desc        -> 'dh_profile:competences:description:<id>' (or ':multi:' variant)
 */
@Pipe({ name: 'competenceField', pure: false })
export class CompetenceFieldPipe implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(
    entry: any,
    field: 'title' | 'desc' | 'teamphase' | 'phase' | 'lowest' | 'teamrole' = 'title',
    variant: 'default' | 'multi' = 'default'
  ): string {
    if (!entry) return '';

    const lang =
      this.translate.currentLang || this.translate.getDefaultLang() || 'nl';

    const nlKey = `${field}_nl`;
    const enKey = `${field}_en`;

    if (lang === 'en' && entry[enKey]) return entry[enKey];
    if (entry[nlKey]) return entry[nlKey];
    if (entry[enKey]) return entry[enKey];

    // Fallback to legacy i18n keys (only for title/desc, which existed pre-ABM)
    const cid = entry.competence_id;
    if (field === 'title') {
      return this.translate.instant(
        variant === 'multi'
          ? `dh_profile:competences:multi:${cid}`
          : `dh_profile:competences:${cid}`
      );
    }
    if (field === 'desc') {
      return this.translate.instant(
        variant === 'multi'
          ? `dh_profile:competences:description:multi:${cid}`
          : `dh_profile:competences:description:${cid}`
      );
    }
    // teamphase, phase, lowest, teamrole have no legacy i18n keys — return empty.
    return '';
  }
}
